/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { FlexItem, FlexLayout } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';

import FilterTypeSelector from './FilterTypeSelector';
import NumericRangeFilter from './NumericRangeFilter';
import SelectValueFilterPane from './SelectValueFilterPane';
import FilterPreferences from './FilterPreferences';
import FilterLoadingSpinner from './FilterLoadingSpinner';
import { FILTER_TYPE } from './filterProps';

import './NumericFilterPane.scss';

/*
This is a common filter component that allows a filter to be applied using a
slider control. There are also options to enter to/from filter values directly
via text boxes, invert the current selection and specify whether to include
blank values. The filter type can be to switched to a 'list' filter which allows the user
to select/deselect from a list of values.
*/
export default class NumericFilterPane extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		// Props unique to NumericFilter
		filterSelectionType: PropTypes.oneOf([FILTER_TYPE.RANGE, FILTER_TYPE.SELECT_VALUE]),
		// Props for NumericRangeFilter
		minimumValue: PropTypes.number,
		maximumValue: PropTypes.number,
		fromValue: PropTypes.number,
		toValue: PropTypes.number,
		includeNulls: PropTypes.bool,
		inverted: PropTypes.bool,
		decimalPlaces: PropTypes.number,
		onChange: PropTypes.func.isRequired,
		onClear: PropTypes.func,
		locale: PropTypes.string,
		// Props for SelectValueFilter
		totalValues: PropTypes.number,
		selectedValuesCount: PropTypes.number,
		loading: PropTypes.bool,
		onClick: PropTypes.func.isRequired,
		onSearch: PropTypes.func.isRequired,
		allowSearch: PropTypes.bool,
		allowIncludeNulls: PropTypes.bool,
		allowSwitch: PropTypes.bool,
		contentRenderer: PropTypes.func,
		getValue: PropTypes.func,
		showLabels: PropTypes.bool,
		showSelectionType: PropTypes.bool,
		showFilterPreferences: PropTypes.bool,
		readOnly: PropTypes.bool,
		hasMoreData: PropTypes.bool,
		invertDisabled: PropTypes.bool,
		isSearching: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		filterSelectionType: FILTER_TYPE.RANGE,
		minimumValue: 0,
		maximumValue: 100,
		fromValue: 0,
		toValue: 0,
		includeNulls: false,
		inverted: false,
		invertDisabled: false,
		decimalPlaces: 5,
		selectedValuesCount: 0,
		showLabels: true,
		allowSearch: true,
		allowIncludeNulls: true,
		allowSwitch: true,
		showSelectionType: true,
		showFilterPreferences: true,
		totalValues: 0,
		readOnly: false,
		locale: undefined,
		hasMoreData: false,
	};

	_onChange = result => {
		// Append the filter type to the result
		const res = {
			...result,
			includeNulls: this.props.includeNulls,
			filterSelectionType: this.props.filterSelectionType,
		};

		this.props.onChange(res);
	};

	// Range filter type has changed, can be FILTER_TYPE.RANGE or FILTER_TYPE.SELECT_VALUE
	_onFilterTypeChanged = filterSelectionType => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			includeNulls: this.props.includeNulls,
			inverted: this.props.inverted,
			filterSelectionType,
		};

		this.props.onChange(result);
	};

	_buildResult = () => {
		const result = {
			fromValue: this.props.fromValue,
			toValue: this.props.toValue,
			includeNulls: this.props.includeNulls,
			inverted: this.props.inverted,
		};

		return result;
	};

	// Preferences have changed
	_onPreferencesChanged = preferences => {
		const result = {
			...this._buildResult(),
			...preferences,
			filterSelectionType: this.props.filterSelectionType,
		};

		this.props.onChange(result);
	};

	_renderNumericFilterPane = () => {
		const {
			fromValue,
			toValue,
			includeNulls,
			inverted,
			showLabels,
			selectedValuesCount,
			minimumValue,
			maximumValue,
			decimalPlaces,
			locale,
			totalValues,
			onClick,
			onSearch,
			getValue,
			readOnly,
			contentRenderer,
			allowSearch,
			filterSelectionType,
			showSelectionType,
			showFilterPreferences,
			allowIncludeNulls,
			allowSwitch,
			hasMoreData,
			invertDisabled,
			isSearching,
		} = this.props;

		const borderStyle = {
			'border-color': '#F2F2F2',
			'border-width': '1px',
			'border-style': 'solid',
		};

		// Define the styles so the when the filter is resized below a certain
		// height the apply button does not overlap with the contents in the
		// filter pane UI
		const containerStyle = {
			overflow: 'hidden',
		};

		return (
			<FlexLayout
				className="bi-filter-numeric-pane"
				justifyContent="space-between"
				fullHeight
				direction="column"
				data-tid="numericFilterPane"
			>
				{allowSwitch && showSelectionType && (
					<FlexItem gutter={[0, 0, 3, 0]}>
						<FilterTypeSelector
							type={filterSelectionType}
							onChange={this._onFilterTypeChanged}
							readOnly={readOnly}
						/>
					</FlexItem>
				)}
				{filterSelectionType === FILTER_TYPE.RANGE && (
					<FlexItem
						className="bi-filter-numeric-pane__instructions"
						gutter={[0, 0, 1, 1]}
					>
						<b>{stringResource.get('sliderDirections')}</b>
					</FlexItem>
				)}
				{filterSelectionType === FILTER_TYPE.RANGE ? (
					<FlexItem grow={1} style={containerStyle}>
						<NumericRangeFilter
							minimumValue={minimumValue}
							maximumValue={maximumValue}
							fromValue={fromValue}
							toValue={toValue}
							inverted={inverted}
							decimalPlaces={decimalPlaces}
							locale={locale}
							onChange={this._onChange}
							showLabels={showLabels}
							readOnly={readOnly}
						/>
					</FlexItem>
				) : (
					<FlexItem grow={1} style={borderStyle}>
						<SelectValueFilterPane
							totalValues={totalValues}
							selectedValuesCount={selectedValuesCount}
							allowSearch={allowSearch}
							onClick={onClick}
							onChange={this._onChange}
							onSearch={onSearch}
							isSearching={isSearching}
							getValue={getValue}
							contentRenderer={contentRenderer}
							readOnly={readOnly}
							hasMoreData={hasMoreData}
						/>
					</FlexItem>
				)}
				{showFilterPreferences && (
					<FlexItem gutter={[2, 0, 0, 0]}>
						<FilterPreferences
							inverted={inverted}
							includeNulls={includeNulls}
							onChange={this._onPreferencesChanged}
							onClear={this.props.onClear}
							showIncludeNull={
								allowIncludeNulls && filterSelectionType === FILTER_TYPE.RANGE
							}
							selectedValuesCount={selectedValuesCount}
							returnToTree={() => {}}
							readOnly={readOnly}
							invertDisabled={invertDisabled}
						/>
					</FlexItem>
				)}
			</FlexLayout>
		);
	};

	render() {
		const { loading } = this.props;

		if (loading) {
			return <FilterLoadingSpinner />;
		}

		return this._renderNumericFilterPane();
	}
}
